import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../ui/dialog';
import { resolveBracket } from '../../../../lib/bracket';
import {
  advancementCut,
  applyTieBreak,
  bracketFor,
  hasVoteCategory,
  stageStandings,
  withCommunityVotes,
} from '../../../../lib/stages';
import { completeStage } from '../../../../database/tournamentStages';
import { handleFirebaseError } from '../../../../utils/errorHandler';

// StageManager only mounts this once a stage is selected, so `stages[index]`
// is always defined here.
const CompleteStageDialog = ({
  open,
  onOpenChange,
  tournamentId,
  tournament,
  stages,
  index,
  playersById,
  votes,
}) => {
  const stage = stages[index];
  const next = stages[index + 1];
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setPicked([]);
  }, [open]);

  const ranked = votes ? withCommunityVotes(stage, votes) : stage;
  const count = next ? next.advanceCount : 1;
  const standings = stageStandings(ranked);
  const cut = advancementCut(standings, count);

  let blocker = null;
  if (stage.type === 'bracket') {
    const resolved = resolveBracket(
      bracketFor(stage),
      stage.participants || [],
      stage.results || {}
    );
    if (!resolved.complete) blocker = 'Finish every match first.';
  }
  if (
    !blocker &&
    hasVoteCategory(stage) &&
    (tournament?.votingOpen !== false || tournament?.communityAverageVisible !== true)
  ) {
    blocker = 'Close community voting and reveal the average first.';
  }
  if (!blocker && standings.length === 0) {
    blocker = 'Nobody has a result yet.';
  }

  const unranked =
    stage.type === 'leaderboard' ? (stage.participants || []).length - standings.length : 0;

  let final = null;
  try {
    final = applyTieBreak(standings, count, picked);
  } catch {
    final = null;
  }

  const nameOf = (id) => playersById[id]?.name || 'Removed player';

  const handleConfirm = async () => {
    if (!final) return;
    setBusy(true);
    try {
      await completeStage(
        tournamentId,
        stage.id,
        final.standings,
        next ? { id: next.id, participants: final.advancing } : null
      );
      onOpenChange(false);
    } catch (err) {
      toast.error('Could not complete the stage.', { description: handleFirebaseError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Complete {stage.name}</DialogTitle>
        </DialogHeader>

        {blocker && <p className="text-sm text-destructive">{blocker}</p>}

        {!blocker && unranked > 0 && (
          <div className="rounded-md border border-warning/40 px-3.5 py-2.5 text-sm text-warning">
            {unranked} player{unranked === 1 ? '' : 's'} have no result. They rank last and can't
            advance.
          </div>
        )}

        {!blocker && cut.tied.length > 0 && (
          <div className="space-y-2 rounded-md border border-warning/40 px-3.5 py-2.5 text-sm text-warning">
            <p>
              {cut.tied.length} players are tied at rank{' '}
              {standings.find((s) => s.playerId === cut.tied[0])?.rank}. Pick {cut.needed} to{' '}
              {next ? `advance to ${next.name}` : 'win the tournament'}.
            </p>
            <ul className="space-y-1">
              {cut.tied.map((id) => {
                const checked = picked.includes(id);
                return (
                  <li key={id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-heat"
                      checked={checked}
                      disabled={!checked && picked.length >= cut.needed}
                      aria-label={`Advance ${nameOf(id)}`}
                      onChange={() =>
                        setPicked((p) => (checked ? p.filter((x) => x !== id) : [...p, id]))
                      }
                    />
                    <span>{nameOf(id)}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {!blocker && final && (
          <div className="space-y-2 text-sm">
            {next ? (
              <>
                <p className="text-muted-foreground">
                  These players start {next.name} (seed order)
                </p>
                <ol className="list-inside list-decimal">
                  {final.advancing.map((id) => (
                    <li key={id}>{nameOf(id)}</li>
                  ))}
                </ol>
              </>
            ) : (
              <p className="font-medium">Champion: {nameOf(final.advancing[0])}</p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" disabled={!!blocker || !final || busy} onClick={handleConfirm}>
            {busy
              ? 'Working…'
              : next
                ? `Complete & advance ${final ? final.advancing.length : count}`
                : 'Complete & crown champion'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CompleteStageDialog;
